import { ArrowUpRight, ArrowDown } from "lucide-react";
import CountUp from "@/components/CountUp";
import { useT } from "@/lib/i18n";

export const Hero = () => {
  const t = useT();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full h-[100svh] min-h-[860px] overflow-hidden bg-black"
    >
      {/* Background image — Trommelverzinken (echtes Fritz Kamm AG Bild) */}
      <div className="absolute inset-0">
        <img
          src="/media/Trommelverzinken-blau-1030x684.jpg"
          alt="Trommelverzinken in der Galvanik der Fritz Kamm AG"
          className="absolute inset-0 w-full h-full object-cover kenburns"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent" />
        <div className="noise" />
      </div>

      {/* Side rail */}
      <div className="hidden md:flex absolute left-6 top-0 bottom-0 flex-col items-center justify-between py-28 z-10">
        <div className="overline rotate-180 [writing-mode:vertical-rl]">
          MELS · ST. GALLEN · CH
        </div>
        <div className="w-px flex-1 bg-white/10 my-6" />
        <div className="overline rotate-180 [writing-mode:vertical-rl]">
          ISO 9001 · ISO 14001
        </div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-16 h-full flex flex-col justify-end pt-28 md:pt-24 pb-16 md:pb-24 lg:pb-32">
        <div className="max-w-5xl">
          <div className="overline mb-6 reveal flex items-center gap-3 text-zinc-300">
            <span className="inline-block w-8 h-px bg-white/40" />
            {t("hero.overline", "FRITZ KAMM AG · Galvanik · Schweiz")}
          </div>

          <h1 className="font-display reveal reveal-delay-1 text-[44px] sm:text-6xl lg:text-[88px] xl:text-[100px] leading-[0.92] font-semibold tracking-[-0.04em] text-white">
            {t("hero.headline_1", "Ihr Partner für")}
            <br />
            <span className="outline-text">{t("hero.headline_2", "Metallveredelungen")}</span>
            <br />
            <span className="italic font-light">{t("hero.headline_3", "und Material-Finishing.")}</span>
          </h1>

          <p className="reveal reveal-delay-2 mt-8 max-w-2xl text-zinc-300 text-base sm:text-lg leading-relaxed">
            {t(
              "hero.sub",
              "Die Fritz Kamm AG zählt zu den führenden Galvanik-Unternehmen der Schweiz. Unser Leitziel — Qualität und Termin — bestimmt jeden Arbeitsschritt."
            )}
          </p>

          <div className="reveal reveal-delay-3 mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href="#kontakt"
              data-testid="hero-cta-offerte"
              className="group inline-flex items-center justify-center gap-2 bg-white text-black px-7 py-4 text-sm font-semibold tracking-tight hover:bg-zinc-200 transition-colors duration-300 shine"
            >
              {t("hero.cta_primary", "Offerte einholen")}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#verfahren"
              data-testid="hero-cta-verfahren"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white px-7 py-4 text-sm font-semibold tracking-tight hover:bg-white/5 hover:border-white/50 transition-all duration-300"
            >
              {t("hero.cta_secondary", "Verfahren ansehen")}
              <ArrowDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-black/45 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 md:px-16 py-5 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-10">
          {[
            { v: "11", labelKey: "hero.stat_verfahren", labelFallback: "Veredelungsverfahren" },
            { v: "9001", prefix: "ISO ", labelKey: "hero.stat_iso", labelFallback: "& ISO 14001 zertifiziert" },
            { v: "1968", labelKey: "hero.stat_tradition", labelFallback: "Schweizer Tradition seit" },
            { v: "CO₂", labelKey: "hero.stat_co2", labelFallback: "Emissionen reduziert" },
          ].map((s, i) => (
            <div
              key={s.labelKey}
              className={`reveal reveal-delay-${i + 1} flex md:flex-col items-baseline md:items-start gap-2`}
            >
              <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-white tabular-nums">
                <CountUp value={s.v} prefix={s.prefix || ""} />
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">
                {t(s.labelKey, s.labelFallback)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
